/* ─────────────────────────────────────────────────────────
 * RESPONSIVE HEADER SCROLL STORYBOARD
 *
 *    0ms   header remains visible at the top of the page
 *  + 8px   downward intent hides the header over 180ms
 *  + 4px   upward intent reveals the header over 260ms
 *    0ms   open navigation, search, keyboard focus or width changes reveal it
 * ───────────────────────────────────────────────────────── */

const SCROLL_INTENT = {
  revealDistance: 4,
  hideDistance: 8,
};
const controllers = new Map();

const getScrollPosition = () => Math.max(0, window.scrollY);
const isPageScrollSuspended = () =>
  document.body.classList.contains('modal-scroll-locked') ||
  document.body.classList.contains('modal-scroll-restoring');

class HeaderScrollController {
  constructor(header) {
    this.header = header;
    this.shell = header.closest('.shopify-section') || header.parentElement;
    this.disclosures = Array.from(header.querySelectorAll('details'));
    this.compactViewport = window.matchMedia('(max-width: 1199px)');
    this.abortController = new AbortController();
    this.frame = 0;
    this.instantFrame = 0;
    this.previousPosition = getScrollPosition();
    this.viewportWidth = window.innerWidth;
    this.direction = null;
    this.directionDistance = 0;

    if (!this.shell) return;

    this.shell.setAttribute('data-header-scroll-shell', '');
    this.setScrolledState(this.previousPosition);
    this.setState('visible');
    this.bindEvents();
  }

  bindEvents() {
    const { signal } = this.abortController;

    window.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
    window.addEventListener('pageshow', () => this.reset({ reveal: true }), { signal });
    window.addEventListener('resize', () => this.handleResize(), { passive: true, signal });
    this.header.addEventListener('focusin', () => {
      if (isPageScrollSuspended()) {
        this.resetIntent();
        return;
      }

      this.reset({ reveal: true, instant: true });
    }, { signal });
    this.header.addEventListener('keydown', () => this.reset({ reveal: true, instant: true }), { signal });
    this.disclosures.forEach((disclosure) => {
      disclosure.addEventListener('toggle', () => this.reset({ reveal: true, instant: true }), { signal });
    });
    document.addEventListener('sigma:modal-scroll-restoring', () => {
      this.shell?.setAttribute('data-header-scroll-restoring', '');
      this.resetIntent();
    }, { signal });
    document.addEventListener('sigma:modal-scroll-restored', () => {
      this.reset();
      this.shell?.removeAttribute('data-header-scroll-restoring');
    }, { signal });
  }

  requestUpdate() {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  }

  update() {
    // Body scroll locking temporarily reports the page at scroll position zero.
    // Preserve the existing header state until the saved page position is restored.
    if (isPageScrollSuspended()) {
      this.resetIntent();
      return;
    }

    const position = getScrollPosition();
    const delta = position - this.previousPosition;
    this.previousPosition = position;
    this.setScrolledState(position);

    if (this.hasOpenInteraction() || this.hasKeyboardFocus() || document.body.getAttribute('aria-busy') === 'true') {
      this.setState('visible', { instant: true });
      this.resetIntent();
      return;
    }

    if (position <= this.header.offsetHeight) {
      this.setState('visible');
      this.resetIntent();
      return;
    }

    if (delta === 0) return;

    const nextDirection = delta > 0 ? 'down' : 'up';

    if (nextDirection !== this.direction) {
      this.direction = nextDirection;
      this.directionDistance = 0;
    }

    this.directionDistance += Math.abs(delta);

    const threshold = nextDirection === 'up' ? SCROLL_INTENT.revealDistance : SCROLL_INTENT.hideDistance;

    if (this.directionDistance < threshold) return;

    this.setState(nextDirection === 'up' ? 'visible' : 'hidden');
    this.directionDistance = 0;
  }

  setState(state, { instant = false } = {}) {
    if (!this.shell) return;

    if (this.shell.dataset.headerScrollState === state) return;

    if (instant) this.setInstantTransition();
    this.shell.dataset.headerScrollState = state;
  }

  setScrolledState(position) {
    this.shell?.toggleAttribute('data-header-scroll-scrolled', position > 0);
  }

  hasKeyboardFocus() {
    const activeElement = document.activeElement;

    return (
      this.header.contains(activeElement) &&
      !activeElement?.hasAttribute('data-pointer-focus') &&
      activeElement?.matches(':focus-visible')
    );
  }

  hasOpenInteraction() {
    const selector = this.compactViewport.matches
      ? '[data-header-menu][open]'
      : '[data-desktop-disclosure][open], [data-header-search][data-search-open="true"], [data-header-search][data-search-closing="true"]';

    return Boolean(this.header.querySelector(selector));
  }

  handleResize() {
    if (window.innerWidth === this.viewportWidth) return;

    this.viewportWidth = window.innerWidth;
    this.reset({ reveal: true, instant: true });
  }

  setInstantTransition() {
    if (!this.shell) return;

    cancelAnimationFrame(this.instantFrame);
    this.shell.setAttribute('data-header-scroll-instant', '');

    this.instantFrame = requestAnimationFrame(() => {
      this.instantFrame = requestAnimationFrame(() => {
        this.shell?.removeAttribute('data-header-scroll-instant');
        this.instantFrame = 0;
      });
    });
  }

  reset({ reveal = false, instant = false } = {}) {
    this.previousPosition = getScrollPosition();
    this.setScrolledState(this.previousPosition);
    this.resetIntent();

    if (reveal) this.setState('visible', { instant });
  }

  resetIntent() {
    this.direction = null;
    this.directionDistance = 0;
  }

  destroy() {
    this.abortController.abort();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.instantFrame);
    this.shell?.removeAttribute('data-header-scroll-shell');
    this.shell?.removeAttribute('data-header-scroll-state');
    this.shell?.removeAttribute('data-header-scroll-scrolled');
    this.shell?.removeAttribute('data-header-scroll-instant');
    this.shell?.removeAttribute('data-header-scroll-restoring');
  }
}

const initializeHeaders = (root = document) => {
  root.querySelectorAll('[data-header-scroll-reveal]').forEach((header) => {
    if (controllers.has(header)) return;
    controllers.set(header, new HeaderScrollController(header));
  });
};

initializeHeaders();

document.addEventListener('shopify:section:load', (event) => initializeHeaders(event.target));

document.addEventListener('shopify:section:unload', (event) => {
  controllers.forEach((controller, header) => {
    if (!event.target.contains(header)) return;
    controller.destroy();
    controllers.delete(header);
  });
});
